/**
 * IndustryPicker — one-click template setup with single-industry lock.
 *
 * Groups all 50 industries by category, highlights the currently-locked
 * industry with a "Current business type" pill, and shows a clear
 * confirmation dialog when the subscriber tries to switch industries
 * (blends verticals like "dental + pet grooming" are prevented).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Stethoscope, HeartPulse, Droplets, Eye, Activity, Bone, Brain, TestTube,
  PawPrint, Scissors, Home, Route, Sparkle, Palette, Circle, Dumbbell, Flower, Flower2,
  Trophy, Waves, GraduationCap, Music, Car, Scale, Calculator, Briefcase, Compass, Camera,
  Utensils, Bed, KeyRound, PartyPopper, Landmark, SprayCan, TreePine, Thermometer,
  Building, Laptop, Wrench, Hand, Heart, Search, Check, Lock, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const ICON_MAP = {
  Sparkles, Stethoscope, HeartPulse, Droplets, Eye, Activity, Bone, Brain, TestTube,
  PawPrint, Scissors, Home, Route, Sparkle, Palette, Circle, Dumbbell, Flower, Flower2,
  Trophy, Waves, GraduationCap, Music, Car, Scale, Calculator, Briefcase, Compass, Camera,
  Utensils, Bed, KeyRound, PartyPopper, Landmark, SprayCan, TreePine, Thermometer,
  Building, Laptop, Wrench, Hand, Heart,
};

const API = process.env.REACT_APP_BACKEND_URL;

export default function IndustryPicker({ onSetupComplete }) {
  const [data, setData] = useState({ categories: [], industries: [] });
  const [search, setSearch] = useState('');
  const [installing, setInstalling] = useState(null);
  const [currentKey, setCurrentKey] = useState(null);
  const [currentLabel, setCurrentLabel] = useState(null);
  const [switchDialog, setSwitchDialog] = useState(null); // { requestedKey, message, services, bookings }
  const [staleBackend, setStaleBackend] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadProfile = () => {
    axios.get(`${API}/api/booking/profile`, auth()).then((r) => {
      if (r?.data?.industry_key) {
        setCurrentKey(r.data.industry_key);
        setCurrentLabel(r.data.industry_label || r.data.industry_key);
      } else {
        setCurrentKey(null);
        setCurrentLabel(null);
      }
    }).catch((e) => {
      if (e?.response?.status === 404 || e?.response?.status === 405) {
        setStaleBackend(true);
      }
    });
  };

  useEffect(() => {
    axios.get(`${API}/api/booking/industries`).then((r) => setData(r.data)).catch(() => {});
    loadProfile();
    // v2.6.30 — Also refresh when tab regains focus so switching sub-tabs
    // and returning to Setup always shows the up-to-date "Current" pill.
    const onVis = () => { if (document.visibilityState === 'visible') loadProfile(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', loadProfile);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', loadProfile);
    };
  }, []);

  const install = async (key, confirmWipe = false, accountPassword = '') => {
    setInstalling(key);
    try {
      const body = { industry_key: key, confirm_wipe: confirmWipe };
      if (accountPassword) body.account_password = accountPassword;
      await axios.post(
        `${API}/api/booking/setup`,
        body,
        auth(),
      );
      const requestedLabel = data.industries.find((i) => i.key === key)?.label || key;
      setCurrentKey(key);
      setCurrentLabel(requestedLabel);
      toast.success(
        confirmWipe
          ? `Switched to ${requestedLabel}. Old services archived — booking history preserved.`
          : `${requestedLabel} is now your booking business type. Resources, services and reminders are ready.`
      );
      setSwitchDialog(null);
      setShowChangeArea(false);
      onSetupComplete?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // v2.6.28 — 409 industry_locked → confirmation dialog
      if (e.response?.status === 409 && typeof detail === 'object' && detail?.error === 'industry_locked') {
        setSwitchDialog({
          requestedKey: detail.requested_industry_key,
          requestedLabel: detail.requested_industry_label,
          currentLabel: detail.current_industry_label,
          message: detail.message,
          servicesCount: detail.services_count,
          bookingsCount: detail.bookings_count,
          password: '',
        });
      } else if (e.response?.status === 401 && typeof detail === 'object' && (detail?.error === 'password_required' || detail?.error === 'password_invalid')) {
        toast.error(detail.message || 'Password required');
        // keep the dialog open so the user can retry
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Setup failed');
      }
    } finally {
      setInstalling(null);
    }
  };

  const filtered = data.industries.filter(
    (i) => !search || i.label.toLowerCase().includes(search.toLowerCase()) || i.category.toLowerCase().includes(search.toLowerCase())
  );

  // 2026-07-26 — Once an industry is locked, hide the picker grid entirely
  // to remove clutter. A discreet "Change industry (settings)" toggle
  // re-opens the full grid, but the actual switch still requires password.
  const [showChangeArea, setShowChangeArea] = useState(false);
  const showPicker = !currentKey || showChangeArea;

  return (
    <div className="space-y-6" data-testid="industry-picker">
      {currentKey && (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="mt-0.5 rounded-full bg-emerald-100 p-1.5">
            <Lock className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="flex-1 text-sm">
            <div className="font-medium text-emerald-900">
              Current business type: <span className="font-semibold">{currentLabel}</span>
            </div>
            <p className="text-emerald-800 mt-0.5 text-xs">
              Locked in. Your calendar, services and resources are scoped to <strong>{currentLabel}</strong>. Change is protected by your account password so it never happens by accident.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowChangeArea((v) => !v)}
            className="text-[11px] font-medium text-emerald-700 hover:text-emerald-900 underline decoration-emerald-300 hover:decoration-emerald-700 shrink-0"
            data-testid="industry-change-toggle"
          >
            {showChangeArea ? 'Cancel change' : 'Change industry (settings)'}
          </button>
        </div>
      )}

      {showPicker && (
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          className="pl-10"
          placeholder="Search 50+ business types — dental, salon, pet, real estate…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="industry-search"
        />
      </div>
      )}

      {showPicker && data.categories.map((cat) => {
        const rows = filtered.filter((i) => i.category === cat);
        if (rows.length === 0) return null;
        return (
          <div key={cat}>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{cat}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {rows.map((i) => {
                const Icon = ICON_MAP[i.icon] || Sparkles;
                const isCurrent = currentKey === i.key;
                return (
                  <Card
                    key={i.key}
                    className={`transition-shadow cursor-pointer ${isCurrent ? 'ring-2 ring-emerald-500 border-emerald-200 bg-emerald-50/40' : 'hover:shadow-md'}`}
                    onClick={() => !isCurrent && install(i.key)}
                    data-testid={`industry-${i.key}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isCurrent ? 'bg-emerald-100' : 'bg-emerald-50'}`}>
                          <Icon className="h-5 w-5 text-emerald-brand" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-text-primary flex items-center gap-1.5">
                            {i.label}
                            {isCurrent && <Lock className="h-3.5 w-3.5 text-emerald-600" />}
                          </div>
                          <div className="text-xs text-gray-medium mt-1">
                            {i.resource_count} resources · {i.service_count} services · {i.custom_field_count} fields
                          </div>
                        </div>
                        {isCurrent ? (
                          <div className="text-xs font-medium text-emerald-700 whitespace-nowrap">Current</div>
                        ) : installing === i.key ? (
                          <div className="text-xs text-emerald-brand">Installing…</div>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs">
                            <Check className="h-3.5 w-3.5" />
                            {currentKey ? 'Switch' : 'Use'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && (
        <p className="text-sm text-gray-medium text-center py-12">No business types match &ldquo;{search}&rdquo;.</p>
      )}

      <AlertDialog open={!!switchDialog} onOpenChange={(o) => !o && setSwitchDialog(null)}>
        <AlertDialogContent data-testid="industry-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Switch business type?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <span className="block">
                You&apos;re about to switch from <strong>{switchDialog?.currentLabel}</strong> to <strong>{switchDialog?.requestedLabel}</strong>.
              </span>
              <span className="block bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                To keep your calendar clean, we&apos;ll <strong>archive</strong> your {switchDialog?.servicesCount || 0} current service{(switchDialog?.servicesCount || 0) === 1 ? '' : 's'} and seed the new business type&apos;s services. Your {switchDialog?.bookingsCount || 0} existing booking{(switchDialog?.bookingsCount || 0) === 1 ? '' : 's'} stay searchable in reports — nothing is deleted.
              </span>
              <span className="block text-xs text-slate-500">
                Two different business types (e.g. dental clinic + pet grooming) should each get their own workspace so services never blend.
              </span>
              <div className="pt-3 border-t border-slate-200">
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Confirm with your account password
                </label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Your Konnect account password"
                  value={switchDialog?.password || ''}
                  onChange={(e) => setSwitchDialog((s) => ({ ...s, password: e.target.value }))}
                  className="text-sm"
                  data-testid="industry-switch-password"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Required to prevent accidental industry switches. Same password you use to sign in.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="industry-switch-cancel">Keep {switchDialog?.currentLabel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => install(switchDialog.requestedKey, true, switchDialog.password || '')}
              disabled={!switchDialog?.password}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
              data-testid="industry-switch-confirm"
            >
              Yes, switch to {switchDialog?.requestedLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
